import React, { useEffect, useState } from 'react';
import { Input, Modal } from 'antd';

import { MAX_MARKDOWN } from '../../utils/dashboards';

/**
 * A heading's markdown from what was typed: one line with no markdown block
 * marker of its own becomes a heading ("Stock" → "## Stock"); anything else —
 * a line that starts with #, several lines — is kept as written (a note).
 */
export function headingMarkdown(text) {
	const value = typeof text === 'string' ? text.trim() : '';
	if (!value) return '';
	if (/\n/.test(value) || /^(#{1,6}\s|[-*+]\s|\d+\.\s|>)/.test(value)) return value;
	return `## ${value}`;
}

/**
 * **Add heading**, while arranging (charts A6, plan §6.5): a line of text
 * that names a part of the dashboard, or a short note. It is markdown, shown
 * through utils/markdown.js like every rich text in keepr; one plain line is
 * a heading. At most 2,000 characters (keepr-api dashboardUtils MAX_MARKDOWN).
 *
 *   onAdd  (markdown) => void
 */
export default function AddHeadingModal({ open, onAdd, onClose }) {
	const [text, setText] = useState('');
	const [error, setError] = useState(null);
	useEffect(() => {
		if (!open) return;
		setText('');
		setError(null);
	}, [open]);

	const add = () => {
		const markdown = headingMarkdown(text);
		if (!markdown) {
			setError('Type the heading first.');
			return;
		}
		if (markdown.length > MAX_MARKDOWN) {
			setError(`A heading or a note is at most ${MAX_MARKDOWN.toLocaleString('en-US')} characters.`);
			return;
		}
		onAdd(markdown);
		onClose();
	};

	return (
		<Modal open={open} title='Add heading' okText='Add' onOk={add} onCancel={onClose} destroyOnClose className='add-heading-modal'>
			<p className='name-chart-modal__hint'>One line is a heading. Several lines, or a line that starts with #, are kept as you write them.</p>
			<label className='name-chart-modal__label' htmlFor='add-heading-text'>Heading</label>
			<Input.TextArea
				id='add-heading-text'
				autoFocus
				autoSize={{ minRows: 1, maxRows: 6 }}
				maxLength={MAX_MARKDOWN}
				value={text}
				placeholder='Stock'
				onChange={(event) => { setText(event.target.value); setError(null); }}
			/>
			{error ? <div className='name-chart-modal__error' role='alert'>{error}</div> : null}
		</Modal>
	);
}
